import { Fragment, useEffect } from "react";

import { setSearchSeen } from "@/api/endpoints/searches";
import Container from "@/components/Container";
import { FILTER, SORTING_OPTION } from "@/constants";
import Episodes from "@/containers/Episodes";
import { SkeletonMentionCard } from "@/containers/MentionCard";
import ShareModal from "@/containers/ShareModal";
import { useAlert } from "@/contexts/AlertContext";
import { useI18n } from "@/contexts/i18nContext";
import { useMentionCards } from "@/contexts/MentionCardsContext";
import useFilterParams from "@/hooks/useFilterParams";
import AllMessages from "@/views/MentionsView/partials/messages/AllMessages";
import useDateBreakPoints from "@/views/MentionsView/partials/useDateBreakpoints";

import Breakpoint from "../partials/Breakpoint";
import CaughtUp from "../partials/CaughtUp";
import MentionPagination from "../partials/MentionPagination";
import useShowUnreadNotice from "../partials/useShowUnreadNotice";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import { Search } from "@/types/Search";
import { ErrorResponse, SuccessfulResponse } from "@/api/fetcher_v2";
import MentionCardNew from "@components/MentionCardNew";
import { getTags } from "@/api/endpoints/tags";
import { MentionCardsProvider } from "@/containers/MentionCardsContext";

const BREAKPOINT_DATE_FORMAT = "PPP";

const MentionsListView = ({ allowPagination = true }) => {
  const queryClient = useQueryClient();
  const { orgUuid } = useParams<{
    uuid: string;
    orgUuid: string;
  }>();
  const { loading, mentionCards, alert } = useMentionCards();
  const { formatDateRange } = useI18n();
  const { patchAlert } = useAlert();
  const [filters] = useFilterParams();
  const shimIndex = useShowUnreadNotice();
  // Breakpoints for showing date headers, the first one is reserved for the Toolbars title.
  const breakpoints = useDateBreakPoints({ v4: true });

  const sortedByDate =
    !filters.sort_by || filters[FILTER.SORTING] === SORTING_OPTION.DATE;
  const isDateSpan = filters[FILTER.START_DATE] && filters.end_date;

  // "Unsee" the search after 3 seconds
  useEffect(() => {
    if (mentionCards?.length > 0 && alert?.has_unread) {
      const t = setTimeout(() => {
        setSearchSeen(alert.uuid);
        patchAlert(alert.uuid, { has_unread: false });

        // Update the has_unread dot in the sidebar
        const searchesData = queryClient.getQueryData<
          SuccessfulResponse<Search[]> | ErrorResponse<unknown>
        >([orgUuid, "searches"]);

        if (searchesData?.ok && searchesData.data.length) {
          queryClient.setQueryData<SuccessfulResponse<Search[]>>(
            [orgUuid, "searches"],
            {
              ...searchesData,
              ok: true,
              data: searchesData.data.map((search) => {
                if (search.uuid === alert.uuid) {
                  return { ...search, has_unread: false };
                }
                return search;
              }),
            }
          );
        }
      }, 3000);

      return () => {
        clearTimeout(t);
      };
    }
  }, [alert, mentionCards, orgUuid, patchAlert, queryClient]);

  // Server state
  const { data: tagsData } = useQuery({
    queryKey: [orgUuid, "tags"],
    queryFn: () => getTags(),
    refetchOnWindowFocus: false,
  });

  const tags = tagsData?.ok ? tagsData.data : [];

  return (
    <Container center={false}>
      <Breakpoint
        title={
          isDateSpan
            ? formatDateRange(
                filters.start_date,
                filters.end_date,
                BREAKPOINT_DATE_FORMAT
              )
            : breakpoints[0] // grab the first one.
        }
      />
      <Episodes>
        {loading ? (
          <>
            <SkeletonMentionCard />
            <SkeletonMentionCard />
            <SkeletonMentionCard />
          </>
        ) : mentionCards?.length > 0 ? (
          <MentionCardsProvider>
            {mentionCards?.map((mentionCard, i) => {
              return (
                <Fragment key={mentionCard.episode.guid}>
                  {i !== 0 && sortedByDate && !isDateSpan && breakpoints[i] ? (
                    <Breakpoint title={breakpoints[i]} />
                  ) : null}

                  {i === shimIndex && <CaughtUp />}

                  <MentionCardNew
                    mentionCard={mentionCard}
                    orgUuid={orgUuid}
                    tags={tags}
                  />
                </Fragment>
              );
            })}
          </MentionCardsProvider>
        ) : (
          <AllMessages orgUuid={orgUuid} v4 />
        )}
      </Episodes>

      <ShareModal v4 />
      {allowPagination && <MentionPagination disabled={loading} v4={true} />}
    </Container>
  );
};

export default MentionsListView;
